/**
 * DashboardPage -- Resume list dashboard.
 *
 * Uses useResumeStore (ADR-021), List + Panel + LoadingState (ADR-022).
 * Sidebar shows user info and a logout button.
 */
import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore, useAuthActions } from '../state/useAuthStore';
import { useResumeStore, useResumeActions } from '../state/useResumeStore';
import { List } from '../components/ui/List';
import { LoadingState } from '../components/ui/LoadingState';
import { Button } from '../components/ui/Button';
import { logger } from '../services/logger.client';
import type { Resume } from '../types';

const log = logger.child({ module: 'DashboardPage' });

/* ------------------------------------------------------------------ */
/*  Small internal components                                         */
/* ------------------------------------------------------------------ */

function StatusBadge({ status }: { status: Resume['status'] }) {
  return <span className={`badge badge--${status}`}>{status}</span>;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function DashboardPage() {
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const { logout } = useAuthActions();

  const resumes = useResumeStore((s) => s.resumes);
  const isLoading = useResumeStore((s) => s.isLoading);
  const error = useResumeStore((s) => s.error);
  const { fetchResumes, createResume } = useResumeActions();

  /* Fetch resumes on mount */
  useEffect(() => {
    log.info('Dashboard mounted -- fetching resumes');
    void fetchResumes();
  }, [fetchResumes]);

  async function handleNewResume() {
    const resume = await createResume('Untitled Resume');
    if (resume) {
      log.info('Navigating to new resume editor', { id: resume.id });
      navigate(`/resume/${resume.id}`);
    }
  }

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="dashboard">
      {/* ---------- Sidebar ---------- */}
      <aside className="dashboard__sidebar">
        <div className="sidebar__brand">
          <h2 className="sidebar__brand-name">MyManyResumes</h2>
        </div>

        <div className="sidebar__user">
          <div className="sidebar__avatar" aria-hidden="true">
            {user?.name?.charAt(0)?.toUpperCase() ?? user?.email?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <p className="sidebar__user-name">{user?.name ?? 'User'}</p>
          <p className="sidebar__user-email">{user?.email ?? ''}</p>
        </div>

        <nav className="sidebar__nav sidebar__nav--cms">
          <div className="sidebar__nav-section">
            <span className="sidebar__nav-heading">CMS</span>
            <Link to="/cms/sites" className="sidebar__nav-link">
              Sites
            </Link>
            <Link to="/cms/content/career" className="sidebar__nav-link">
              Content Library
            </Link>
          </div>
          <div className="sidebar__nav-bottom">
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Sign Out
            </Button>
          </div>
        </nav>
      </aside>

      {/* ---------- Main content ---------- */}
      <main className="dashboard__main">
        <header className="dashboard__header">
          <h1 className="dashboard__title">My Resumes</h1>
          <Button variant="primary" size="md" onClick={handleNewResume} loading={isLoading}>
            New Resume
          </Button>
        </header>

        <LoadingState loading={isLoading} error={error}>
          <List<Resume>
            items={resumes}
            keyExtractor={(r) => r.id}
            emptyMessage="No resumes yet. Click 'New Resume' to get started."
            className="resume-list"
          >
            {(resume) => (
              <li className="resume-card" onClick={() => navigate(`/resume/${resume.id}`)}>
                <div className="resume-card__header">
                  <h3 className="resume-card__title">{resume.title}</h3>
                  <StatusBadge status={resume.status} />
                </div>
                <p className="resume-card__meta">
                  Updated {formatDate(resume.updatedAt)}
                </p>
              </li>
            )}
          </List>
        </LoadingState>
      </main>
    </div>
  );
}
